import React from "react"
import { useTranslation } from "react-i18next"

import { Anchor, AnchorProps } from "shared/ui/link/Anchor"
import { Readonly } from "shared/ui/readonly/Readonly"
import { TimeDifference } from "shared/ui/time-difference/TimeDifference"

import { ProjectPhaseChip } from "entities/projects/ui/project-phase-chip/ProjectPhaseChip"
import { ProjectRoleChip } from "entities/projects/ui/project-role-chip/ProjectRoleChip"
import { REDUCED_PROJECT_ACTION_TYPES } from "features/project-management/model/useProjectActions"
import { ProjectActionsButton } from "features/project-management/ui/project-actions-button/ProjectActionsButton"
import { ProjectCard } from "features/project-management/ui/project-card/ProjectCard"

import { ProjectsOverviewCardProps } from "../ProjectsOverviewCard"

export interface ProjectsOverviewCardViewModel
  extends Omit<AnchorProps, "ref" | "href" | "data">,
    ProjectsOverviewCardProps {}

export const ProjectsOverviewCardView = React.memo(
  React.forwardRef(
    (
      {
        projectID,
        model,
        isLoading: isLoadingProp,
        ...rest
      }: ProjectsOverviewCardViewModel,
      ref?: React.ForwardedRef<HTMLAnchorElement>
    ) => {
      const { t } = useTranslation()
      const lastChangedByUser = model.core.data.project?.data?.lastChangedBy
      const permissions = model.core.data.permissions?.data

      const isLoading = isLoadingProp || model.core.data.project?.isLoading

      return (
        <Anchor
          {...rest}
          ref={ref}
          href={model.core.data.meta.href}
          className="projects-overview-card"
          data-testid="projects-overview-card"
          disabled={!model.core.handlers.onOpen}
          onClick={model.core.handlers.onOpen ?? undefined}
        >
          <ProjectCard
            className="project-card--overview card--elevated"
            model={model}
          >
            <ProjectCard.Image />
            <ProjectCard.Title />
            {!isLoading ? (
              <ProjectCard.Actions>
                {permissions ? (
                  <ProjectActionsButton
                    className="btn-secondary--muted"
                    allowedActions={REDUCED_PROJECT_ACTION_TYPES}
                    onOpen={model.core.handlers.onOpen}
                    onEdit={model.core.handlers.onEdit}
                    onShowDetails={model.core.handlers.onShowDetails}
                    onShare={model.core.handlers.onShare}
                    onManageTeam={model.team.handlers.onManageTeam}
                    onInviteMember={model.team.handlers.onInviteMember}
                    onExport={model.core.handlers.onExport}
                    onGaebExport={model.core.handlers.onGaebExport}
                    onMusterheftExport={model.core.handlers.onMusterheftExport}
                    onOpenAttachments={
                      model.attachments.handlers.onOpenAttachments
                    }
                    onNewProduct={model.creation?.handlers.onNewProduct}
                    onNewRoom={model.creation?.handlers.onNewRoom}
                    onNewFloor={model.creation?.handlers.onNewFloor}
                    onDelete={model.core.handlers.onDelete}
                    onActivate={model.core.handlers.onActivate}
                    onDuplicate={model.core.handlers.onDuplicate}
                  />
                ) : null}
              </ProjectCard.Actions>
            ) : null}
            <ProjectCard.Chips>
              <ProjectPhaseChip
                phase={model.core.data.project?.data?.phase}
                isLoading={model.core.data.project?.isLoading}
              />
              <ProjectRoleChip
                teamRole={model.core.data.role?.data}
                isLoading={model.core.data.role?.isLoading}
              />
            </ProjectCard.Chips>
            <ProjectCard.Body>
              <ProjectCard.Row>
                <Readonly.Root
                  className="project-card__last-changed"
                  isLoading={model.core.data.project?.isLoading}
                >
                  <Readonly.Label>{t("General.LastChanged")}</Readonly.Label>
                  <Readonly.Value>
                    <TimeDifference
                      timeStamp={model.core.data.project?.data?.lastChanged}
                    />
                  </Readonly.Value>
                </Readonly.Root>
                {lastChangedByUser || isLoading ? (
                  <Readonly.Root isLoading={model.core.data.project?.isLoading}>
                    <Readonly.Label>
                      {t("General.LastChangedBy")}
                    </Readonly.Label>
                    <Readonly.Value>
                      {lastChangedByUser?.name || lastChangedByUser?.email}
                    </Readonly.Value>
                  </Readonly.Root>
                ) : null}
              </ProjectCard.Row>
            </ProjectCard.Body>
          </ProjectCard>
        </Anchor>
      )
    }
  )
)
